import { useCallback } from 'react';
import DatePicker from '@/components/DatePicker';
import { Badge } from "@/components/ui/badge";
import { Button } from '@/components/ui/button';
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Search, Trash2, X as XIcon } from 'lucide-react';
import { getStoresByUF, isUFSelected, isUFPartiallySelected, StoreUF } from '../utils';
import DatePresets from './DatePresets';

interface DashboardFiltersProps {
  startDate?: Date;
  endDate?: Date;
  selectedStores: string[];
  groupByMonth: boolean;
  isFetching: boolean;
  onStartDateChange: (date: Date | null) => void;
  onEndDateChange: (date: Date | null) => void;
  onStoreSelect: (storeId: string) => void;
  onUFSelect: (uf: StoreUF, checked: boolean) => void;
  onGroupByMonthChange: (checked: boolean) => void;
  onRemoveStore: (storeId: string) => void;
  onClearFilters: () => void;
  onSearch: () => void;
}

export default function DashboardFilters({
  startDate,
  endDate,
  selectedStores,
  groupByMonth,
  isFetching,
  onStartDateChange,
  onEndDateChange,
  onStoreSelect,
  onUFSelect,
  onGroupByMonthChange,
  onRemoveStore,
  onClearFilters,
  onSearch,
}: DashboardFiltersProps) {
  const handleStoreSelect = useCallback((storeId: string) => {
    onStoreSelect(storeId);
  }, [onStoreSelect]);

  const handleUFSelect = useCallback((uf: StoreUF, checked: boolean) => {
    onUFSelect(uf, checked);
  }, [onUFSelect]);

  const isFilterEmpty = !startDate && !endDate && selectedStores.length === 0 && !groupByMonth;

  const handlePresetSelect = useCallback((start: Date, end: Date) => {
    onStartDateChange(start);
    onEndDateChange(end);
  }, [onStartDateChange, onEndDateChange]);

  return (
    <Card className="p-6 mb-6">
      <div>
        <DatePresets onPresetSelect={handlePresetSelect} disabled={isFetching} />
        <div className="flex items-end gap-4 mb-2">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data Inicial
            </label>
            <div className="relative">
              <DatePicker
                selected={startDate}
                onChange={onStartDateChange}
                className={`w-full ${isFetching ? 'opacity-50' : ''}`}
                placeholderText="Selecione a data inicial"
                disabled={isFetching}
              />
              {isFetching && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/50">
                  <div className="animate-pulse w-full h-full rounded bg-gray-200" />
                </div>
              )}
            </div>
          </div>

          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data Final
            </label>
            <div className="relative">
              <DatePicker
                selected={endDate}
                onChange={onEndDateChange}
                className={`w-full ${isFetching ? 'opacity-50' : ''}`}
                placeholderText="Selecione a data final"
                minDate={startDate}
                disabled={isFetching}
              />
              {isFetching && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/50">
                  <div className="animate-pulse w-full h-full rounded bg-gray-200" />
                </div>
              )}
            </div>
          </div>

          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Lojas
            </label>
            <div className="relative">
              <Select
                onValueChange={handleStoreSelect}
                disabled={isFetching}
              >
                <SelectTrigger className={`w-full ${isFetching ? 'opacity-50' : ''}`}>
                  <SelectValue placeholder="Selecione as lojas" />
                </SelectTrigger>
                <SelectContent>
                  <ScrollArea className="h-[300px]">
                    {Array.from(getStoresByUF()).map(([uf, stores]) => (
                      <SelectGroup key={uf}>
                        <div className="flex items-center gap-2 px-2 py-1.5">
                          <Checkbox
                            id={`select-all-${uf}`}
                            checked={isUFSelected(uf, selectedStores)}
                            onCheckedChange={(checked) => handleUFSelect(uf, checked === true)}
                            disabled={isFetching}
                            className={isUFPartiallySelected(uf, selectedStores) ? "data-[state=checked]:bg-primary/50" : ""}
                          />
                          <label
                            htmlFor={`select-all-${uf}`}
                            className="text-sm font-semibold cursor-pointer"
                          >
                            {uf} {isUFPartiallySelected(uf, selectedStores) ? "(Parcial)" : ""}
                          </label>
                        </div>
                        {stores
                          .sort((a, b) => Number(a) - Number(b))
                          .map((store) => (
                            <SelectItem
                              key={store}
                              value={store}
                            >
                              Loja {String(store).padStart(3, '0')}
                            </SelectItem>
                          ))}
                      </SelectGroup>
                    ))}
                  </ScrollArea>
                </SelectContent>
              </Select>
              {isFetching && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/50">
                  <div className="animate-pulse w-full h-full rounded bg-gray-200" />
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Switch
                checked={groupByMonth}
                onCheckedChange={onGroupByMonthChange}
                disabled={isFetching}
              />
              <label className="text-sm font-medium text-gray-700">
                Agrupar por Mês
              </label>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={onClearFilters}
                className="flex items-center gap-2"
                disabled={isFilterEmpty || isFetching}
              >
                {isFetching ? (
                  <div className="animate-pulse w-4 h-4 rounded-full bg-gray-300" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                Limpar Filtros
              </Button>
              <Button
                onClick={onSearch}
                className="flex items-center gap-2"
                disabled={isFetching}
              >
                {isFetching ? (
                  <div className="animate-pulse w-4 h-4 rounded-full bg-gray-300" />
                ) : (
                  <Search className="w-4 h-4" />
                )}
                Buscar
              </Button>
            </div>
          </div>
        </div>

        {selectedStores.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {selectedStores.map((storeId) => (
              <Badge
                key={storeId}
                variant="secondary"
                className={`flex items-center gap-1 ${isFetching ? 'opacity-50' : ''}`}
              >
                Loja {String(storeId).padStart(3, '0')}
                <button
                  onClick={() => onRemoveStore(storeId)}
                  className="ml-1 hover:bg-gray-200 rounded-full p-0.5"
                  disabled={isFetching}
                >
                  <XIcon className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}